import React from 'react';
import { LuMapPin } from 'react-icons/lu';
import useBranchAssignment from '../hooks/useBranchAssignment';
import './BranchAssignmentField.css';

/**
 * Branch picker for stock/orders: owner selects; staff see their assigned branch (read-only).
 * Pass `assignment` from useBranchAssignment() when the parent also needs branchId for API calls.
 */
export default function BranchAssignmentField({
  user,
  assignment = null,
  className = '',
  label = 'Branch',
  hint = 'Stock and purchase orders are recorded for this branch.',
  required = true,
}) {
  const internal = useBranchAssignment(user);
  const {
    isOwner,
    branches,
    branchId,
    setBranchId,
    loading,
    ready,
    selectedBranchName,
    staffBranchName,
  } = assignment || internal;

  const rootClass = ['branch-assignment', className].filter(Boolean).join(' ');

  if (!isOwner) {
    return (
      <div className={rootClass}>
        <span className="branch-assignment__label">{label}</span>
        <div className="branch-assignment__readonly" aria-live="polite">
          <LuMapPin size={16} aria-hidden />
          <span>{staffBranchName || '—'}</span>
        </div>
        {!staffBranchName ? (
          <p className="branch-assignment__warn" role="alert">
            Your account has no branch assigned. Ask the owner to set your branch.
          </p>
        ) : (
          <p className="branch-assignment__hint">{hint}</p>
        )}
      </div>
    );
  }

  return (
    <label className={`${rootClass} branch-assignment--owner`}>
      <span className="branch-assignment__label">
        {label}
        {required ? ' *' : ''}
      </span>
      <select
        className="branch-assignment__select"
        value={branchId}
        onChange={(e) => setBranchId(e.target.value)}
        disabled={loading || branches.length === 0}
        required={required}
      >
        <option value="">{loading ? 'Loading branches…' : 'Select branch…'}</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      {branches.length === 0 && !loading ? (
        <p className="branch-assignment__warn" role="alert">
          No branches found. Add a branch first from Branches settings.
        </p>
      ) : (
        <p className="branch-assignment__hint">
          {selectedBranchName ? `${hint} (${selectedBranchName})` : hint}
        </p>
      )}
      {!ready && !loading ? (
        <p className="branch-assignment__warn" role="alert">
          Select which branch receives this stock or order.
        </p>
      ) : null}
    </label>
  );
}

export { useBranchAssignment };
